/**
 * Pure Game Engine Module
 * All game logic separated from React components
 * Handles: Toss, Batting, Bowling, Innings, Scoring, Win/Loss
 */

// Game constants
export const GAME_CONSTANTS = {
  MIN_NUMBER: 0,
  MAX_NUMBER: 6,
  MAX_INNINGS: 2,
  TOSS_OPTIONS: ['bat', 'bowl'],
};

// Game phases
export const GAME_PHASES = {
  TOSS: 'toss',
  FIRST_INNINGS: 'first_innings',
  SECOND_INNINGS: 'second_innings',
  GAME_OVER: 'game_over',
};

/**
 * Initialize a new game state
 */
export const initializeGame = () => {
  return {
    phase: GAME_PHASES?.TOSS,
    currentInnings: 1,
    playerChoice: null,
    aiChoice: null,
    tossWinner: null,
    playerRole: null, // 'batting' or 'bowling'
    aiRole: null,
    playerScore: 0,
    aiScore: 0,
    target: null,
    isPlayerOut: false,
    isAIOut: false,
    ballHistory: [],
    winner: null,
    gameOver: false,
  };
};

/**
 * Simulate coin toss
 * @returns {string} 'player' or 'ai'
 */
export const simulateToss = () => {
  return Math.random() > 0.5 ? 'player' : 'ai';
};

/**
 * Process toss result and set roles
 * @param {object} gameState - Current game state
 * @param {string} tossWinner - 'player' or 'ai'
 * @param {string} tossWinnerChoice - 'bat' or 'bowl'
 * @returns {object} Updated game state
 */
export const processToss = (gameState, tossWinner, tossWinnerChoice) => {
  const newState = { ...gameState };
  newState.tossWinner = tossWinner;
  
  if (tossWinner === 'player') {
    newState.playerRole = tossWinnerChoice === 'bat' ? 'batting' : 'bowling';
    newState.aiRole = tossWinnerChoice === 'bat' ? 'bowling' : 'batting';
  } else {
    newState.aiRole = tossWinnerChoice === 'bat' ? 'batting' : 'bowling';
    newState.playerRole = tossWinnerChoice === 'bat' ? 'bowling' : 'batting';
  }
  
  newState.phase = GAME_PHASES?.FIRST_INNINGS;
  return newState;
};

/**
 * Generate AI number choice based on mood and game state
 * @param {object} aiMood - AI mood configuration
 * @param {object} gameState - Current game state
 * @returns {number} AI's chosen number (0-6)
 */
export const generateAIChoice = (aiMood, gameState) => {
  const { MIN_NUMBER, MAX_NUMBER } = GAME_CONSTANTS;
  
  // Basic AI logic - can be enhanced based on mood
  if (aiMood?.name === 'Chaos') {
    // Completely random
    return Math.floor(Math.random() * (MAX_NUMBER - MIN_NUMBER + 1)) + MIN_NUMBER;
  }
  
  if (aiMood?.name === 'Confident') {
    // Slightly predictable patterns
    const history = gameState?.ballHistory || [];
    if (history?.length > 0) {
      const lastPlayerChoice = history?.[history?.length - 1]?.playerNumber;
      // Try to counter player's last choice
      return (lastPlayerChoice + 3) % 7;
    }
  }
  
  // Default: random choice
  return Math.floor(Math.random() * (MAX_NUMBER - MIN_NUMBER + 1)) + MIN_NUMBER;
};

/**
 * Process a ball (single play)
 * @param {object} gameState - Current game state
 * @param {number} playerNumber - Player's chosen number (0-6)
 * @param {number} aiNumber - AI's chosen number (0-6)
 * @returns {object} Updated game state with ball result
 */
export const processBall = (gameState, playerNumber, aiNumber) => {
  const newState = { ...gameState };
  const isOut = playerNumber === aiNumber;
  
  // Record ball in history
  const ballResult = {
    innings: newState?.currentInnings,
    playerNumber,
    aiNumber,
    isOut,
    playerRole: newState?.playerRole,
  };
  
  newState.ballHistory = [...(newState?.ballHistory || []), ballResult];
  
  // Determine scoring
  if (isOut) {
    // Numbers match - OUT!
    if (newState?.playerRole === 'batting') {
      newState.isPlayerOut = true;
    } else {
      newState.isAIOut = true;
    }
  } else {
    // Score runs based on who is batting
    if (newState?.playerRole === 'batting') {
      newState.playerScore += playerNumber;
    } else {
      newState.aiScore += aiNumber;
    }
  }
  
  // Check if innings should end
  if (newState?.isPlayerOut || newState?.isAIOut) {
    return endInnings(newState);
  }
  
  // Check target in second innings
  if (newState?.currentInnings === 2 && newState?.target) {
    const chasingScore = newState?.playerRole === 'batting' ? newState?.playerScore : newState?.aiScore;
    if (chasingScore > newState?.target) {
      return endInnings(newState);
    }
  }
  
  return newState;
};

/**
 * End current innings and transition to next phase
 * @param {object} gameState - Current game state
 * @returns {object} Updated game state
 */
export const endInnings = (gameState) => {
  const newState = { ...gameState };
  
  if (newState?.currentInnings === 1) {
    // First innings complete - set target and swap roles
    const firstInningsScore = newState?.playerRole === 'batting' ? newState?.playerScore : newState?.aiScore;
    newState.target = firstInningsScore;
    
    // Swap roles
    const tempRole = newState?.playerRole;
    newState.playerRole = newState?.aiRole;
    newState.aiRole = tempRole;
    
    // Reset out flags
    newState.isPlayerOut = false;
    newState.isAIOut = false;
    
    // Move to second innings
    newState.currentInnings = 2;
    newState.phase = GAME_PHASES?.SECOND_INNINGS;
  } else {
    // Second innings complete - determine winner
    newState.phase = GAME_PHASES?.GAME_OVER;
    newState.gameOver = true;
    newState.winner = determineWinner(newState);
  }
  
  return newState;
};

/**
 * Determine the winner of the game
 * @param {object} gameState - Current game state
 * @returns {string} 'player', 'ai', or 'tie'
 */
export const determineWinner = (gameState) => {
  if (gameState?.playerScore > gameState?.aiScore) {
    return 'player';
  } else if (gameState?.aiScore > gameState?.playerScore) {
    return 'ai';
  }
  return 'tie';
};

/**
 * Calculate gem reward based on game result
 * @param {object} gameState - Final game state
 * @param {number} consecutiveWins - Player's consecutive wins
 * @returns {number} Gems earned
 */
export const calculateGemReward = (gameState, consecutiveWins) => {
  if (gameState?.winner !== 'player') return 0;
  
  const baseReward = 10;
  const streakBonus = Math.min(consecutiveWins * 5, 50);
  const scoreBonus = Math.floor(gameState?.playerScore / 10);
  
  return baseReward + streakBonus + scoreBonus;
};

/**
 * Get AI dialogue based on game state and mood
 * @param {object} aiMood - AI mood configuration
 * @param {string} situation - 'toss_win', 'toss_lose', 'batting', 'bowling', 'out', 'winning', 'losing'
 * @returns {string} AI dialogue
 */
export const getAIDialogue = (aiMood, situation) => {
  const dialogues = aiMood?.dialogues || {};
  
  const situationMap = {
    toss_win: dialogues?.intro || "I won the toss. Let's begin.",
    toss_lose: dialogues?.intro || "You won the toss. Make your choice.",
    batting: dialogues?.taunt || "Let's see if you can get me out.",
    bowling: dialogues?.taunt || "Try to score against me.",
    out: dialogues?.winning || "OUT! That's how it's done.",
    winning: dialogues?.winning || "I'm ahead. Can you catch up?",
    losing: dialogues?.losing || "You're doing well... for now.",
  };
  
  return situationMap?.[situation] || dialogues?.taunt || "Let's play.";
};

/**
 * Validate player number choice
 * @param {number} number - Player's chosen number
 * @returns {boolean} Is valid
 */
export const isValidNumber = (number) => {
  return Number.isInteger(number) && 
         number >= GAME_CONSTANTS?.MIN_NUMBER && 
         number <= GAME_CONSTANTS?.MAX_NUMBER;
};

function GameEngine(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: GameEngine is not implemented yet.', args);
  return null;
}

export default GameEngine;